import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { Capacitor } from "@capacitor/core";
import { App } from "./App";
import { initKeyboardInset } from "./lib/keyboardInset";
import { resolveTokenColor, isDarkColor } from "./utils/nativeColor";
import "./styles/globals.css";
import "./styles/tokens.css";
import "./styles/driver-tokens.css";

// Track the on-screen keyboard height in --kb-inset so fixed sheets/overlays
// lift above it (web/PWA + any platform where the WebView doesn't resize).
initKeyboardInset();

// Capacitor native shell branding: status-bar tint + explicit splash hide.
// Web builds skip both — Capacitor.isNativePlatform() returns false in
// the browser so the dynamic imports never load on web.
if (Capacitor.isNativePlatform()) {
  void (async () => {
    try {
      const { StatusBar, Style } = await import("@capacitor/status-bar");
      // Tint the bar with the app's own ground, and pick the icon style to
      // match it. Capacitor's Style is named for the BACKGROUND, not the
      // icons: Style.Light = light background → DARK icons, which is right on
      // the light shell; in dark mode the ground is near-black and the icons
      // have to flip or they are invisible.
      //
      // 🔴 The colour must be RESOLVED, not `var(--bg)`. Android hands it to
      // Color.parseColor, which cannot read a CSS variable — the call rejected
      // and the bar silently kept the system's default dark tint under dark
      // icons, which is the exact regression the old comment warned about.
      const ground = resolveTokenColor("--bg", "#F2F2F7");
      await StatusBar.setStyle({
        style: isDarkColor(ground) ? Style.Dark : Style.Light,
      });
      if (Capacitor.getPlatform() === "android") {
        await StatusBar.setBackgroundColor({ color: ground });
        // Android 15 (API 35+) makes edge-to-edge mandatory; setOverlaysWebView
        // becomes a no-op. We handle the status-bar offset in CSS via
        // env(safe-area-inset-top) on #root (globals.css) — works on every
        // Android version and on iOS for free.
      }
    } catch (err) {
      // status-bar plugin missing or call rejected — log and continue.
      // Don't block app boot on cosmetics.
      console.warn("[boot] StatusBar setup failed", err);
    }
    try {
      const { SplashScreen } = await import("@capacitor/splash-screen");
      // Fade out over 300ms once the React tree has begun rendering.
      // launchAutoHide=false in capacitor.config.ts means the splash
      // would hold forever otherwise.
      await SplashScreen.hide({ fadeOutDuration: 300 });
    } catch (err) {
      console.warn("[boot] SplashScreen.hide failed", err);
    }
  })();
}

// Clerk publishable key — loaded from Vite env at build time. Publishable
// keys are meant to ship in the frontend bundle; they're distinct from the
// server-side `sk_*` secret key, which lives in the backend-only .env.
//
// Strict in production: a prod build with no key is a misconfigured deploy
// and we fail loudly up front rather than render a broken sign-in screen.
//
// Lenient in dev / CI: CI doesn't have the env var and Playwright's smoke
// test only cares that React mounts at all. Falling back to a dummy key
// keeps Clerk's provider from throwing during bootstrap — it'll log
// network errors when the widget tries to reach Clerk, but the rest of
// the app renders fine, which is what the smoke test actually asserts.
const DEV_FALLBACK_KEY = 'pk_test_Zm9yLXRlc3RzLW9ubHktbm9uLWZ1bmN0aW9uYWwuY2xlcmsuYWNjb3VudHMuZGV2JA';
const clerkPublishableKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
  ?? (import.meta.env.DEV ? DEV_FALLBACK_KEY : undefined);

if (!clerkPublishableKey) {
  throw new Error(
    'Missing VITE_CLERK_PUBLISHABLE_KEY. Add pk_test_... (dev) or pk_live_... (prod) to .env.local / deploy env before building.',
  );
}


ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      // signInUrl + signUpUrl tell Clerk where our custom pages live so
      // any SDK-driven "redirect to sign in" lands on our Attic-chromed
      // `<SignIn />` / `<SignUp />` widgets instead of Clerk's hosted
      // Account Portal. Without these, Clerk defaults to the Portal
      // (subdomain.clerk.accounts.dev) and users see no Attic branding.
      // Dashboard → Paths in Clerk needs to match for these to stick in
      // every hosted flow; those settings win over these props.
      signInUrl="/login"
      signUpUrl="/signup"
      // Match our app's token storage semantics: Clerk session tokens live
      // in cookies under the clerk.accounts.dev / clerk.com domain by default;
      // we're fine with that since we only use Clerk for identity verification
      // and immediately swap Clerk's token for our own Harper Bearer.
      afterSignOutUrl="/login"
    >
      <App />
    </ClerkProvider>
  </React.StrictMode>
);
